import { useState, useEffect } from 'react'
import styled, { keyframes } from 'styled-components'
import { api } from '../../services/api'
import { Spinner } from './StyledComponents'

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

const slideIn = keyframes`
  from {
    transform: translateY(-20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: ${fadeIn} 0.2s ease-out;
`

const Modal = styled.div`
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  width: 95%;
  max-width: 550px;
  max-height: 85vh;
  overflow-y: auto;
  animation: ${slideIn} 0.3s ease-out;
`

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.lg};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border-radius: ${({ theme }) => theme.borderRadius.lg} ${({ theme }) => theme.borderRadius.lg} 0 0;
`

const ModalTitle = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSizes.lg};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  opacity: 0.8;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
`

const ModalBody = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
`

const Section = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};

  &:last-child {
    margin-bottom: 0;
  }
`

const SectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.primary};
  margin: 0 0 ${({ theme }) => theme.spacing.md} 0;
  padding-bottom: ${({ theme }) => theme.spacing.sm};
  border-bottom: 2px solid ${({ theme }) => theme.colors.secondary};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.xl};
`

const DocumentCard = styled.div`
  background: ${({ theme }) => theme.colors.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  transition: all 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }

  &:last-child {
    margin-bottom: 0;
  }
`

const DocumentInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`

const DocumentIcon = styled.span`
  font-size: 1.5rem;
`

const DocumentName = styled.span`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};
`

const DownloadButton = styled.button`
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  cursor: pointer;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`

const EmptyMessage = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.lg};
  color: ${({ theme }) => theme.colors.textMuted};
  background: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-style: italic;
`

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border-left: 4px solid ${({ theme }) => theme.colors.primary};
`

const UserInfoRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.fontSizes.sm};
`

const UserInfoLabel = styled.span`
  color: ${({ theme }) => theme.colors.textMuted};
  min-width: 100px;
`

const UserInfoValue = styled.span`
  color: ${({ theme }) => theme.colors.text};
  font-weight: 500;
`

interface Certificate {
    name: string
    file_path?: string
}

interface Title {
    name: string
    institution?: string
    date_obtained?: string
    file_path?: string
}

interface UserProfile {
    id: number
    username: string
    display_name: string
    department: string
    job_role: string
    years_experience: number
    cv_filename: string | null
    certificates: Certificate[]
    titles: Title[]
}

interface UserDocumentsModalProps {
    isOpen: boolean
    onClose: () => void
    userId: number
    userName: string
    isAdmin?: boolean
}

export const UserDocumentsModal: React.FC<UserDocumentsModalProps> = ({
    isOpen,
    onClose,
    userId,
    userName,
    isAdmin = false,
}) => {
    const [loading, setLoading] = useState(true)
    const [profile, setProfile] = useState<UserProfile | null>(null)
    const [downloading, setDownloading] = useState<string | null>(null)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        if (isOpen && userId) {
            loadProfile()
        }
    }, [isOpen, userId])

    const loadProfile = async () => {
        setLoading(true)
        setError(null)
        try {
            const response = await api.users.getProfile(userId)
            setProfile(response.data)
        } catch (err) {
            console.error('Error loading profile:', err)
            setError('Error al cargar el perfil del usuario')
        } finally {
            setLoading(false)
        }
    }

    const getDownloadError = (err: any, defaultMsg: string): string => {
        const status = err.response?.status
        if (status === 401) return 'Sesión expirada. Recarga la página e intenta de nuevo.'
        if (status === 403) return 'No tienes permisos para descargar este archivo.'
        if (status === 413) return 'El archivo es demasiado grande. Descarga documentos individualmente.'
        if (status === 404) return 'El archivo no está disponible.'
        return defaultMsg
    }

    const validateBlobResponse = (response: any): boolean => {
        const contentType = response.headers?.['content-type'] || ''
        if (contentType.includes('text/html') || response.data.size === 0) {
            setError('Error: la respuesta del servidor no es un archivo válido. Recarga la página e intenta de nuevo.')
            return false
        }
        return true
    }

    const triggerDownload = (blob: Blob, filename: string) => {
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
    }

    const handleDownloadCV = async () => {
        if (!profile) return

        setDownloading('cv')
        try {
            const response = await api.users.getCV(userId)
            if (!validateBlobResponse(response)) return

            // Get filename from content-disposition or use default
            const contentDisposition = response.headers?.['content-disposition']
            let filename = profile.cv_filename || `${userName.replace(/\s+/g, '_')}_CV.pdf`
            if (contentDisposition) {
                const match = contentDisposition.match(/filename="?([^";\n]+)"?/)
                if (match) filename = match[1]
            }

            triggerDownload(new Blob([response.data]), filename)
        } catch (err: any) {
            setError(getDownloadError(err, 'Error al descargar el CV'))
        } finally {
            setDownloading(null)
        }
    }

    const handleDownloadCertificate = async (index: number, certName: string) => {
        setDownloading(`cert-${index}`)
        try {
            const response = await api.users.getCertificateFile(userId, index)
            if (!validateBlobResponse(response)) return

            const contentType = response.headers?.['content-type'] || ''
            let ext = '.pdf'
            if (contentType.includes('jpeg') || contentType.includes('jpg')) ext = '.jpg'
            else if (contentType.includes('png')) ext = '.png'
            else if (contentType.includes('webp')) ext = '.webp'

            const filename = `${userName.replace(/\s+/g, '_')}_${certName.replace(/\s+/g, '_')}${ext}`
            triggerDownload(new Blob([response.data]), filename)
        } catch (err: any) {
            setError(getDownloadError(err, `Error al descargar el certificado "${certName}"`))
        } finally {
            setDownloading(null)
        }
    }

    const handleDownloadTitle = async (index: number, titleName: string) => {
        setDownloading(`title-${index}`)
        try {
            const response = await api.users.getTitleFile(userId, index)
            if (!validateBlobResponse(response)) return

            const contentType = response.headers?.['content-type'] || ''
            let ext = '.pdf'
            if (contentType.includes('jpeg') || contentType.includes('jpg')) ext = '.jpg'
            else if (contentType.includes('png')) ext = '.png'
            else if (contentType.includes('webp')) ext = '.webp'

            const filename = `${userName.replace(/\s+/g, '_')}_${titleName.replace(/\s+/g, '_')}${ext}`
            triggerDownload(new Blob([response.data]), filename)
        } catch (err: any) {
            setError(getDownloadError(err, `Error al descargar el título "${titleName}"`))
        } finally {
            setDownloading(null)
        }
    }

    const handleDownloadAll = async () => {
        setDownloading('all')
        try {
            const response = await api.users.getDocumentsZip(userId)
            if (!validateBlobResponse(response)) return

            triggerDownload(new Blob([response.data]), `${userName.replace(/\s+/g, '_')}_documents.zip`)
        } catch (err: any) {
            setError(getDownloadError(err, 'Error al descargar los documentos'))
        } finally {
            setDownloading(null)
        }
    }

    const handleDownloadAllUsers = async (docType?: string) => {
        const key = `bulk-${docType || 'all'}`
        setDownloading(key)
        try {
            const response = await api.users.getAllDocumentsZip(docType)
            if (!validateBlobResponse(response)) return

            const label = docType || 'all'
            triggerDownload(new Blob([response.data]), `all_users_${label}_documents.zip`)
        } catch (err: any) {
            setError(getDownloadError(err, 'Error al descargar los documentos'))
        } finally {
            setDownloading(null)
        }
    }

    if (!isOpen) return null

    return (
        <Overlay onClick={onClose}>
            <Modal onClick={(e) => e.stopPropagation()}>
                <ModalHeader>
                    <ModalTitle>
                        👤 {userName}
                    </ModalTitle>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        {isAdmin && (
                            <DownloadButton
                                onClick={handleDownloadAll}
                                disabled={downloading === 'all'}
                                style={{ fontSize: '0.8rem' }}
                            >
                                {downloading === 'all' ? '⏳' : '📦'} Descargar Todo
                            </DownloadButton>
                        )}
                        <CloseButton onClick={onClose}>&times;</CloseButton>
                    </div>
                </ModalHeader>

                <ModalBody>
                    {loading ? (
                        <LoadingContainer>
                            <Spinner />
                        </LoadingContainer>
                    ) : error ? (
                        <EmptyMessage>{error}</EmptyMessage>
                    ) : profile ? (
                        <>
                            {/* User Info Summary */}
                            <UserInfo>
                                {profile.job_role && (
                                    <UserInfoRow>
                                        <UserInfoLabel>🏷️ Rol:</UserInfoLabel>
                                        <UserInfoValue>{profile.job_role}</UserInfoValue>
                                    </UserInfoRow>
                                )}
                                {profile.department && (
                                    <UserInfoRow>
                                        <UserInfoLabel>🏢 Departamento:</UserInfoLabel>
                                        <UserInfoValue>{profile.department}</UserInfoValue>
                                    </UserInfoRow>
                                )}
                                {profile.years_experience !== undefined && profile.years_experience !== null && (
                                    <UserInfoRow>
                                        <UserInfoLabel>📅 Experiencia:</UserInfoLabel>
                                        <UserInfoValue>{profile.years_experience} años</UserInfoValue>
                                    </UserInfoRow>
                                )}
                            </UserInfo>

                            {/* CV Section */}
                            <Section>
                                <SectionTitle>📄 Curriculum Vitae</SectionTitle>
                                {profile.cv_filename ? (
                                    <DocumentCard>
                                        <DocumentInfo>
                                            <DocumentIcon>📑</DocumentIcon>
                                            <DocumentName>{profile.cv_filename}</DocumentName>
                                        </DocumentInfo>
                                        {isAdmin && (
                                            <DownloadButton
                                                onClick={handleDownloadCV}
                                                disabled={downloading === 'cv'}
                                            >
                                                {downloading === 'cv' ? '⏳' : '⬇️'} Descargar
                                            </DownloadButton>
                                        )}
                                    </DocumentCard>
                                ) : (
                                    <EmptyMessage>
                                        Este usuario no ha subido su CV
                                    </EmptyMessage>
                                )}
                            </Section>

                            {/* Certificates Section */}
                            <Section>
                                <SectionTitle>🏅 Certificados</SectionTitle>
                                {profile.certificates && profile.certificates.length > 0 ? (
                                    profile.certificates.map((cert, index) => (
                                        <DocumentCard key={index}>
                                            <DocumentInfo>
                                                <DocumentIcon>🎓</DocumentIcon>
                                                <DocumentName>{cert.name}</DocumentName>
                                            </DocumentInfo>
                                            {isAdmin && cert.file_path ? (
                                                <DownloadButton
                                                    onClick={() => handleDownloadCertificate(index, cert.name)}
                                                    disabled={downloading === `cert-${index}`}
                                                >
                                                    {downloading === `cert-${index}` ? '⏳' : '⬇️'} Descargar
                                                </DownloadButton>
                                            ) : !isAdmin ? null : (
                                                <span style={{ color: '#999', fontSize: '0.85rem' }}>
                                                    Sin archivo
                                                </span>
                                            )}
                                        </DocumentCard>
                                    ))
                                ) : (
                                    <EmptyMessage>
                                        Este usuario no ha registrado certificados
                                    </EmptyMessage>
                                )}
                            </Section>

                            {/* Titles Section */}
                            <Section>
                                <SectionTitle>🏛️ Títulos</SectionTitle>
                                {profile.titles && profile.titles.length > 0 ? (
                                    profile.titles.map((title, index) => (
                                        <DocumentCard key={index}>
                                            <DocumentInfo>
                                                <DocumentIcon>📜</DocumentIcon>
                                                <div>
                                                    <DocumentName>{title.name}</DocumentName>
                                                    {title.institution && (
                                                        <span style={{ fontSize: '0.8rem', color: '#666' }}>{title.institution}</span>
                                                    )}
                                                </div>
                                            </DocumentInfo>
                                            {isAdmin && title.file_path ? (
                                                <DownloadButton
                                                    onClick={() => handleDownloadTitle(index, title.name)}
                                                    disabled={downloading === `title-${index}`}
                                                >
                                                    {downloading === `title-${index}` ? '⏳' : '⬇️'} Descargar
                                                </DownloadButton>
                                            ) : !isAdmin ? null : (
                                                <span style={{ color: '#999', fontSize: '0.85rem' }}>
                                                    Sin archivo
                                                </span>
                                            )}
                                        </DocumentCard>
                                    ))
                                ) : (
                                    <EmptyMessage>
                                        Este usuario no ha registrado títulos
                                    </EmptyMessage>
                                )}
                            </Section>

                            {/* Bulk download all users - admin only */}
                            {isAdmin && (
                                <Section>
                                    <SectionTitle>📦 Descarga masiva (todos los perfiles)</SectionTitle>
                                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                        <DownloadButton
                                            onClick={() => handleDownloadAllUsers()}
                                            disabled={downloading?.startsWith('bulk-') || false}
                                        >
                                            {downloading === 'bulk-all' ? '⏳' : '📦'} Todo
                                        </DownloadButton>
                                        <DownloadButton
                                            onClick={() => handleDownloadAllUsers('cv')}
                                            disabled={downloading?.startsWith('bulk-') || false}
                                        >
                                            {downloading === 'bulk-cv' ? '⏳' : '📄'} CVs
                                        </DownloadButton>
                                        <DownloadButton
                                            onClick={() => handleDownloadAllUsers('certificados')}
                                            disabled={downloading?.startsWith('bulk-') || false}
                                        >
                                            {downloading === 'bulk-certificados' ? '⏳' : '🏅'} Certificados
                                        </DownloadButton>
                                        <DownloadButton
                                            onClick={() => handleDownloadAllUsers('titulos')}
                                            disabled={downloading?.startsWith('bulk-') || false}
                                        >
                                            {downloading === 'bulk-titulos' ? '⏳' : '🏛️'} Títulos
                                        </DownloadButton>
                                    </div>
                                </Section>
                            )}
                        </>
                    ) : null}
                </ModalBody>
            </Modal>
        </Overlay>
    )
}
